import React from 'react'
import moment from 'moment'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import { Text } from '../elementals'
import { Modal } from '../..'
import Icon from '../elementals/icon'
import styles from './styles.scss'

const cx = classnames.bind(styles)

interface SignatureStatusProps {
  firstName?: string
  recipientStatus: graph.enums.RecipientStatus
  signed?: Date | null
  position?: number
}

interface TrackingModalProps {
  people: graph.enums.DocusignRecipientTracking[]
  isOpen: boolean
  title?: string
  onCloseModal?: () => void
}

const STATUS_VISUALS: Record<string, { iconName: string; color: string }> = {
  [graph.enums.RecipientStatus.Completed]: {
    iconName: 'fa-check-circle',
    color: 'success',
  },
} as const

const DEFAULT_VISUAL = {
  iconName: 'fa-exclamation-circle',
  color: 'caution',
}

/**
 * Displays signature status for a recipient including their name and signature date
 * @param props - SignatureStatus component props
 */
const SignatureStatus: React.FC<SignatureStatusProps> = ({
  firstName = '',
  recipientStatus,
  signed,
  position = 0,
}) => {
  const visuals = STATUS_VISUALS[recipientStatus] || DEFAULT_VISUAL
  const signatureLine = signed
    ? `Signed ${moment(signed).format('M/D/Y')}`
    : 'Pending'

  return (
    <div className={cx('person-status')}>
      <div>
        <Text color={visuals.color}>
          <Icon size='large' name={visuals.iconName} color='inherit' />
          <span className={cx('name')} data-elm-id={`name_${position}`}>
            {firstName || 'Unknown'}
          </span>
        </Text>
      </div>
      <div className={cx('date-signed')}>
        <Text color={visuals.color} data-elm-id={`date_signed_${position}`}>
          {signatureLine}
        </Text>
      </div>
    </div>
  )
}

/**
 * Modal displaying tracking information for document recipients
 * @param props - TrackingModal component props
 */
const TrackingModal: React.FC<TrackingModalProps> = ({
  people,
  isOpen,
  title = 'Purchase Contract Signatures',
  onCloseModal,
}) => {
  if (!people?.length) {
    return null
  }

  return (
    <Modal
      elmId='tracking_modal'
      open={isOpen}
      onClose={onCloseModal}
      size='tiny'
      title={title}
    >
      <div className={cx('modal-container')}>
        {people.map((person, index) => (
          <SignatureStatus
            key={person.recipient_party?.party_id || `signature-${index}`}
            firstName={person.recipient_party?.first_name as string}
            recipientStatus={
              person.recipient_status as graph.enums.RecipientStatus
            }
            signed={person.signed as Date}
            position={index}
          />
        ))}
      </div>
    </Modal>
  )
}

export default TrackingModal
export type { SignatureStatusProps, TrackingModalProps }
